import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { backendService } from "../../../services/backend.service";
import type { PbjModel, PbjModelUpsertBody } from "../../../utils/models";
import { PBJ_MODEL_QUERY_KEY } from "./usePbjModel";

export const useUpsertPbjModel = () => {
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const { updateUserInfo } = useAuth();
	const { t } = useTranslation();

	return useMutation<PbjModel, Error, PbjModelUpsertBody>({
		mutationFn: async (body) => {
			const res = await backendService.upsertPbjModel(body);
			return res.data;
		},
		onSuccess: async (row) => {
			// The saved row is the source of truth for the prediction inputs; refresh
			// userInfo so chosenDepartment.pbj_model reflects the new values without a
			// page reload. Prediction/audit caches are still invalidated below — they're
			// computed outputs keyed by (dept, year, quarter), not the row itself.
			await updateUserInfo();
			queryClient.invalidateQueries({ queryKey: [PBJ_MODEL_QUERY_KEY, "prediction"] });
			queryClient.invalidateQueries({ queryKey: [PBJ_MODEL_QUERY_KEY, "audit"] });
			showSnackbar(t("pbj_model.config.saved"), "success");
			track("pbj_model_config_updated_success", {
				department_id: row.departmentId,
			});
		},
		onError: (error) => {
			showSnackbar(t("pbj_model.config.save.failed"), "error");
			trackError("pbj_model_config_updated_error", {
				error_message: error?.message,
			});
		},
	});
};
